import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { Capacitor } from '@capacitor/core';
import { Haptics } from '@capacitor/haptics';
import type { TopBarFeature } from '../types';
import { useScreenOrientation, useWindowSize } from '@vueuse/core';

type UiState = {
  noDisturbMode: boolean;
  animationDuration: number;
  controlsHideTimer: any;
  controlsHideTimerDuration: number;
};

export const MAX_ZOOM_FACTOR = 4;
const DEFAULT_ZOOM_FACTOR = 1;
export const MIN_ZOOM_FACTOR = 1;

//Widths based on Vuetify display dimensions
const DEFAULT_SCREEN_WIDTHS = {
  xs: 600,
  sm: 840,
  md: 1145,
  lg: 1545,
  xl: 2138,
  xxl: 3840,
};

const useUiStore = defineStore('book-player-ui', () => {
  const { height } = useWindowSize();
  const { orientation } = useScreenOrientation();

  const uiState = ref<UiState>({
    noDisturbMode: false,
    animationDuration: 0.5,
    controlsHideTimer: null,
    controlsHideTimerDuration: 3000,
  });

  const isMobileLandscape = computed(() => {
    if (!orientation.value) return false;
    return orientation.value.includes('landscape') && height.value <= DEFAULT_SCREEN_WIDTHS['xs'];
  });

  const isSmAndDownLandscape = computed(() => {
    if (!orientation.value) return false;
    return orientation.value.includes('landscape') && height.value <= DEFAULT_SCREEN_WIDTHS['sm'];
  });

  const isLandscape = computed(() => {
    if (!orientation.value) return false;
    return orientation.value.includes('landscape');
  });

  const isLanguageMenuVisible = ref(false);
  const isTableOfContentVisible = ref(false);
  const isControlsVisible = ref(true);
  const isChevronsVisible = ref(true);
  const isAnnotationsDrawerVisible = ref(false); // Unified drawer for bookmarks + highlights
  const isSelectingText = ref(false);
  const isAnnotationBarVisible = ref(false);
  const highlightColor = ref('pink');
  const isPlaybackRateVisible = ref(false);
  const isVolumeSliderVisible = ref(false);
  const isSettingsVisible = ref(false);
  const isPageTurnVisible = ref(false);
  const isReaderVisible = ref(true);
  const isGoToStartVisible = ref(false);
  const isTimeBasedSeekingVisible = ref(false);
  const isRightFrameVisible = ref(false);

  const opaqueMenus = ref(false);
  const alwaysShowControls = ref(false);
  const alwaysShowSettings = ref(false);
  const dyslexicFontAvailable = ref(false);
  const themeMode = ref<string>('light');
  const resetTabbing = ref(false);
  const isLockScreenOverlayVisible = ref(false);
  const lockScreenAvailable = ref(false);
  const bookPlayerZoomFactor = ref(DEFAULT_ZOOM_FACTOR);
  const playerXDragOffset = ref(0);
  const playerYDragOffset = ref(0);
  const isLongPress = ref(false);
  const switchModeAvailable = ref(true);
  const isPageRangeEditing = ref(false);
  const isHoveringPageRange = ref(false);
  const showLastPosition = ref(false);
  const topBar = ref<TopBarFeature>({ show: true });
  const topBarHeight = ref(0);
  const bottomBarHeight = ref(0);

  const noDisturbMode = computed(() => uiState.value.noDisturbMode);
  const animationDuration = computed(() => uiState.value.animationDuration);
  const controlsHideTimer = computed(() => uiState.value.controlsHideTimer);
  const isTopBarTransparent = computed(() => topBar.value.transparent ?? false);
  const isFullscreen = ref(false);

  // Setup fullscreen change listener to keep isFullscreen reactive
  const handleFullscreenChange = () => {
    isFullscreen.value = !!document.fullscreenElement;
  };

  // Add event listeners for fullscreen changes
  if (typeof document !== 'undefined') {
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);
  }

  // Cleanup function to remove fullscreen event listeners
  const cleanupFullscreenListeners = () => {
    if (typeof document !== 'undefined') {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    }
  };

  const toggleNoDisturbMode = (toggle: boolean) => {
    uiState.value.noDisturbMode = toggle;
  };
  const toggleLanguageMenu = (toggle: boolean) => {
    console.debug('toggleLanguageMenu', toggle);
    isLanguageMenuVisible.value = toggle;
  };
  const openLanguageMenu = () => {
    isLanguageMenuVisible.value = true;
  };
  const closeLanguageMenu = () => {
    isLanguageMenuVisible.value = false;
  };
  const toggleTableOfContentDrawer = (toggle: boolean) => {
    isTableOfContentVisible.value = toggle;
    isAnnotationsDrawerVisible.value = false;
  };
  const openTableOfContentDrawer = () => {
    isTableOfContentVisible.value = true;
  };
  const closeTableOfContentDrawer = () => {
    isTableOfContentVisible.value = false;
  };

  const toggleAnnotationsDrawer = (toggle: boolean) => {
    isAnnotationsDrawerVisible.value = toggle;
    isTableOfContentVisible.value = false;
  };
  const openAnnotationsDrawer = () => {
    isAnnotationsDrawerVisible.value = true;
  };
  const closeAnnotationsDrawer = () => {
    isAnnotationsDrawerVisible.value = false;
  };

  const toggleVisiblePageTurnVisible = (toggle: boolean) => {
    isPageTurnVisible.value = toggle;
  };

  const toggleVisiblePlaybackRate = (toggle: boolean) => {
    isPlaybackRateVisible.value = toggle;
  };

  const closeVisiblePlaybackRate = () => {
    isPlaybackRateVisible.value = false;
  };

  const toggleVisibleSettings = (toggle: boolean) => {
    isSettingsVisible.value = toggle;
  };

  const closeVisibleSettings = () => {
    isSettingsVisible.value = false;
  };

  const toggleVisibleVolumeSlider = (toggle: boolean) => {
    isVolumeSliderVisible.value = toggle;
  };

  const setOpaqueMenus = (value: boolean) => {
    opaqueMenus.value = value;
  };

  const setAlwaysShowControls = (toggle: boolean) => {
    alwaysShowControls.value = toggle;
  };

  const setAlwaysShowSettings = (toggle: boolean) => {
    alwaysShowSettings.value = toggle;
  };

  const setDyslexicFontAvailable = (toggle: boolean) => {
    dyslexicFontAvailable.value = toggle;
  };

  const setThemeMode = (mode: 'light' | 'dark') => {
    themeMode.value = mode;
  };

  const setResetTabbing = (toggle: boolean) => {
    resetTabbing.value = toggle;
  };

  const toggleVisibleLockScreenOverlay = (toggle: boolean) => {
    isLockScreenOverlayVisible.value = toggle;
  };

  const setLockScreenAvailable = (value: boolean) => {
    lockScreenAvailable.value = value;
  };

  const setSwitchModeAvailable = (value: boolean) => {
    switchModeAvailable.value = value;
  };

  const setIsPageRangeEditing = (value: boolean) => {
    isPageRangeEditing.value = value;
  };

  const setIsHoveringPageRange = (value: boolean) => {
    isHoveringPageRange.value = value;
  };

  const setShowLastPosition = (value: boolean) => {
    showLastPosition.value = value;
  };

  const setTopBar = (value: TopBarFeature) => {
    topBar.value = value;
  };

  const setReaderVisible = (value: boolean) => {
    isReaderVisible.value = value;
  };

  const toggleVisibleGoToStart = (toggle: boolean) => {
    isGoToStartVisible.value = toggle;
  };

  const toggleVisibleTimeBasedSeeking = (toggle: boolean) => {
    isTimeBasedSeekingVisible.value = toggle;
  };

  const setTopBarHeight = (value: number) => {
    topBarHeight.value = value;
  };

  const setBottomBarHeight = (value: number) => {
    bottomBarHeight.value = value;
  };

  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
      // Update ref as backup (event listener should handle this, but just in case)
      isFullscreen.value = !!document.fullscreenElement;
    } catch (error) {
      console.error('Error toggling fullscreen:', error);
    }
  };

  const exitFullscreen = async () => {
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
        isFullscreen.value = false;
      }
    } catch (error) {
      console.error('Error exiting fullscreen:', error);
    }
  };

  const toggleVisibleRightFrame = (toggle: boolean) => {
    isRightFrameVisible.value = toggle;
  };

  const allMenusClosed = computed(
    () =>
      !isLanguageMenuVisible.value &&
      !isTableOfContentVisible.value &&
      !isAnnotationsDrawerVisible.value &&
      !isSettingsVisible.value &&
      !isVolumeSliderVisible.value &&
      !isPageTurnVisible.value &&
      !isPlaybackRateVisible.value &&
      !isRightFrameVisible.value,
  );

  const hideControlsAfterTimeout = () => {
    // Clear any existing timer
    if (uiState.value.controlsHideTimer) {
      clearTimeout(uiState.value.controlsHideTimer);
      uiState.value.controlsHideTimer = null;
    }

    uiState.value.controlsHideTimer = setTimeout(() => {
      if (
        alwaysShowControls.value ||
        !allMenusClosed.value ||
        isPageRangeEditing.value ||
        isHoveringPageRange.value ||
        isSelectingText.value
      ) {
        return;
      }
      closeTableOfContentDrawer();
      isControlsVisible.value = false;
      isPageTurnVisible.value = false;
      isPlaybackRateVisible.value = false;
      isSettingsVisible.value = false;
      isVolumeSliderVisible.value = false;
      uiState.value.controlsHideTimer = null;
    }, uiState.value.controlsHideTimerDuration);
  };

  const toggleDrawerAndControls = (hideAfterTimeout = true, toggle?: boolean) => {
    if (
      alwaysShowControls.value ||
      isLockScreenOverlayVisible.value ||
      isSelectingText.value ||
      isPageRangeEditing.value ||
      isHoveringPageRange.value
    ) {
      return;
    }

    // Determine whether to show or hide controls
    const shouldShow = toggle !== undefined ? toggle : !isControlsVisible.value;

    // Don't hide controls if any menu is open
    if (!shouldShow && !allMenusClosed.value) {
      return;
    }

    // Clear any existing hide timer immediately
    if (uiState.value.controlsHideTimer) {
      clearTimeout(uiState.value.controlsHideTimer);
      uiState.value.controlsHideTimer = null;
    }

    // Toggle controls visibility
    isControlsVisible.value = shouldShow;

    // If showing controls and should auto-hide after timeout, restart the timer
    if (shouldShow && hideAfterTimeout) {
      hideControlsAfterTimeout();
    }
  };

  const $reset = () => {
    if (uiState.value.controlsHideTimer) {
      clearTimeout(uiState.value.controlsHideTimer);
    }
    uiState.value = {
      noDisturbMode: true,
      animationDuration: 0.5,
      controlsHideTimer: null,
      controlsHideTimerDuration: 2000,
    };
    isLanguageMenuVisible.value = false;
    isTableOfContentVisible.value = false;
    isControlsVisible.value = true;
    isChevronsVisible.value = true;
    isAnnotationsDrawerVisible.value = false;
    isPlaybackRateVisible.value = false;
    isVolumeSliderVisible.value = false;
    isSettingsVisible.value = false;
    isPageTurnVisible.value = false;
    isLockScreenOverlayVisible.value = false;
    isLongPress.value = false;
    switchModeAvailable.value = true;
    showLastPosition.value = false;
    topBar.value = { show: true };
    isRightFrameVisible.value = false;
  };

  const resetState = () => {
    $reset();
  };

  const resetPlayerTransform = () => {
    bookPlayerZoomFactor.value = DEFAULT_ZOOM_FACTOR;
    playerXDragOffset.value = 0;
    playerYDragOffset.value = 0;
  };

  const transformPlayer = (newScale: number, xOffset: number, yOffset: number) => {
    if (newScale > MAX_ZOOM_FACTOR || newScale < MIN_ZOOM_FACTOR) {
      console.warn('trying to zoom past limits');
      return;
    }

    // TODO - do not allow zooming while the player is flipping

    bookPlayerZoomFactor.value = newScale;
    playerXDragOffset.value = xOffset;
    playerYDragOffset.value = yOffset;

    /*
    We limit the max panning
     */
    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;

    const maxXTranslation = (screenWidth * (bookPlayerZoomFactor.value - 1)) / 2;
    const maxYTranslation = (screenHeight * (bookPlayerZoomFactor.value - 1)) / 2;

    playerXDragOffset.value = Math.min(Math.max(playerXDragOffset.value, -maxXTranslation), maxXTranslation);
    playerYDragOffset.value = Math.min(Math.max(playerYDragOffset.value, -maxYTranslation), maxYTranslation);

    /*
    We reset the player's position when the user zooms back to default.
    We account for some JavaScript floating point imprecision by checking if the zoom factor is close to the default value.
     */
    if (Math.abs(bookPlayerZoomFactor.value - DEFAULT_ZOOM_FACTOR) < 0.01) {
      resetPlayerTransform();
    }
  };

  const isPlayerZoomed = computed(() => bookPlayerZoomFactor.value !== 1);

  let pressTimer: any = null;

  const startPress = async (event: MouseEvent | TouchEvent) => {
    const isTouch = event.type === 'touchstart';

    // prevent long-press context menu on touch devices
    if (isTouch) event.preventDefault();
    console.debug('isTouch', isTouch);

    pressTimer = setTimeout(async () => {
      const platform = Capacitor.getPlatform();
      if (platform === 'ios' || platform === 'android') {
        await Haptics.vibrate();
      }
      isLongPress.value = false; // TODO: set to true when long press is enabled for context-menu
    }, 500); // 500ms for long press detection
  };

  const endPress = (callback?: () => void) => {
    clearTimeout(pressTimer!);
    if (isLongPress.value) {
      isLongPress.value = false;
    } else {
      if (callback) {
        console.debug('calling Callback');
        callback();
      }
    }
  };

  return {
    themeMode,
    animationDuration,
    isControlsVisible,
    isChevronsVisible,
    controlsHideTimer,
    isTableOfContentVisible,
    isLanguageMenuVisible,
    noDisturbMode,
    isAnnotationsDrawerVisible,
    isSelectingText,
    isAnnotationBarVisible,
    highlightColor,
    isPlaybackRateVisible,
    isVolumeSliderVisible,
    isSettingsVisible,
    isPageTurnVisible,
    isLockScreenOverlayVisible,
    isRightFrameVisible,
    lockScreenAvailable,
    opaqueMenus,
    dyslexicFontAvailable,
    allMenusClosed,
    resetTabbing,
    bookPlayerZoomFactor,
    playerXDragOffset,
    playerYDragOffset,
    isPlayerZoomed,
    isLongPress,
    switchModeAvailable,
    showLastPosition,
    isTopBarTransparent,
    isReaderVisible,
    isGoToStartVisible,
    isTimeBasedSeekingVisible,
    isFullscreen,
    isMobileLandscape,
    isSmAndDownLandscape,
    isLandscape,
    topBarHeight,
    bottomBarHeight,
    setThemeMode,
    openLanguageMenu,
    closeLanguageMenu,
    toggleTableOfContentDrawer,
    openTableOfContentDrawer,
    closeTableOfContentDrawer,
    toggleNoDisturbMode,
    hideControlsAfterTimeout,
    toggleAnnotationsDrawer,
    openAnnotationsDrawer,
    closeAnnotationsDrawer,
    toggleDrawerAndControls,
    toggleLanguageMenu,
    toggleVisiblePageTurnVisible,
    toggleVisiblePlaybackRate,
    toggleVisibleSettings,
    toggleVisibleVolumeSlider,
    toggleVisibleLockScreenOverlay,
    toggleVisibleGoToStart,
    toggleVisibleRightFrame,
    resetState,
    setAlwaysShowControls,
    alwaysShowControls,
    setAlwaysShowSettings,
    alwaysShowSettings,
    setDyslexicFontAvailable,
    setOpaqueMenus,
    setResetTabbing,
    setLockScreenAvailable,
    transformPlayer,
    resetPlayerTransform,
    closeVisibleSettings,
    closeVisiblePlaybackRate,
    endPress,
    startPress,
    setSwitchModeAvailable,
    setShowLastPosition,
    isPageRangeEditing,
    setIsPageRangeEditing,
    isHoveringPageRange,
    setIsHoveringPageRange,
    setTopBar,
    setReaderVisible,
    toggleVisibleTimeBasedSeeking,
    setTopBarHeight,
    setBottomBarHeight,
    toggleFullscreen,
    exitFullscreen,
    cleanupFullscreenListeners,
  };
});

export default useUiStore;
